import { useState } from "react";
import axios from "axios";
import PageShell from "@/components/site/PageShell";
import SEO from "@/components/site/SEO";
import { CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AREAS = [
  "Tecnologia / Engenharia",
  "Produto / Design",
  "Curadoria Jurídica",
  "Comercial",
  "Marketing",
  "Sucesso do Cliente",
  "Outra",
];

export default function FacaParte() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    area: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");
    try {
      const res = await axios.post(`${API}/careers`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        area: form.area,
        message: form.message.trim() || null,
      });
      if (res.status === 200) {
        setStatus("success");
        setForm({ name: "", email: "", phone: "", area: "", message: "" });
      } else {
        throw new Error("Erro no envio");
      }
    } catch (err) {
      setStatus("error");
      setErrorMsg("Não foi possível enviar sua candidatura. Tente novamente em instantes.");
    }
  };

  return (
    <>
      <SEO
        title="Faça parte da nossa equipe | licito.guru"
        description="Vagas para especialistas jurídicos, engenheiros de software, cientistas de dados, designers e sucesso do cliente na licito.guru. Junte-se ao time que está reinventando as contratações públicas no Brasil."
        path="/faca-parte"
      />
    <PageShell
      kicker=""
      title="Faça parte da nossa equipe."
      lead="Estamos revolucionando as licitações públicas no país. Se você quer participar dessa jornada, conte um pouco sobre você."
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-6 md:mt-10">
        <div className="lg:col-span-7">
          {status === "success" ? (
            <div
              data-testid="careers-success"
              className="border hairline rounded-md p-8 md:p-10 bg-[#F7F8FA]"
            >
              <CheckCircle2 size={28} className="text-[#2EE59D]" strokeWidth={2} />
              <h3 className="mt-4 font-display text-[#1A3A61] text-2xl md:text-3xl leading-tight">
                Candidatura recebida.
              </h3>
              <p className="mt-3 text-[#1A3A61]/70 text-[15px] leading-relaxed max-w-md">
                Obrigado por seu interesse em fazer parte da licito.guru. Nossa equipe
                vai analisar suas informações e entrará em contato caso haja
                aderência a uma vaga aberta.
              </p>
              <button
                onClick={() => setStatus("idle")}
                data-testid="careers-reset"
                className="mt-6 btn-secondary"
              >
                Enviar outra candidatura
              </button>
            </div>
          ) : (
            <form
              data-testid="careers-form"
              onSubmit={handleSubmit}
              className="grid grid-cols-1 md:grid-cols-2 gap-5"
            >
              <div className="md:col-span-1">
                <label className="kicker block mb-2">Nome</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={set("name")}
                  data-testid="careers-input-name"
                  className="w-full border hairline bg-white rounded-md px-4 py-3 text-[15px] text-[#1A3A61] focus:outline-none focus:border-[#00C2D1]"
                />
              </div>
              <div className="md:col-span-1">
                <label className="kicker block mb-2">E-mail</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  data-testid="careers-input-email"
                  className="w-full border hairline bg-white rounded-md px-4 py-3 text-[15px] text-[#1A3A61] focus:outline-none focus:border-[#00C2D1]"
                />
              </div>
              <div className="md:col-span-1">
                <label className="kicker block mb-2">Telefone (opcional)</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  data-testid="careers-input-phone"
                  placeholder="(00) 00000-0000"
                  className="w-full border hairline bg-white rounded-md px-4 py-3 text-[15px] text-[#1A3A61] focus:outline-none focus:border-[#00C2D1]"
                />
              </div>
              <div className="md:col-span-1">
                <label className="kicker block mb-2">Área de interesse</label>
                <select
                  required
                  value={form.area}
                  onChange={set("area")}
                  data-testid="careers-input-area"
                  className="w-full border hairline bg-white rounded-md px-4 py-3 text-[15px] text-[#1A3A61] focus:outline-none focus:border-[#00C2D1]"
                >
                  <option value="" disabled>
                    Selecione uma área
                  </option>
                  {AREAS.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="kicker block mb-2">
                  Conte um pouco sobre você (opcional)
                </label>
                <textarea
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  data-testid="careers-input-message"
                  placeholder="Sua experiência, motivação ou link do LinkedIn / currículo."
                  className="w-full border hairline bg-white rounded-md px-4 py-3 text-[15px] text-[#1A3A61] focus:outline-none focus:border-[#00C2D1] resize-y"
                />
              </div>

              {errorMsg && (
                <div className="md:col-span-2 text-red-600 text-[14px]">{errorMsg}</div>
              )}

              <div className="md:col-span-2 flex items-center gap-3 pt-2">
                <button
                  type="submit"
                  disabled={status === "loading"}
                  data-testid="careers-submit"
                  className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === "loading" ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Enviando…
                    </>
                  ) : (
                    <>Enviar candidatura</>
                  )}
                </button>
                <span className="text-[12px] text-[#1A3A61]/50">
                  Ao enviar, você concorda com o tratamento dos dados conforme nossa
                  política de LGPD.
                </span>
              </div>
            </form>
          )}
        </div>

        <aside className="lg:col-span-5 lg:pl-10 lg:border-l hairline">
          <span className="kicker">O que buscamos</span>
          <h3 className="mt-3 font-display text-[#1A3A61] text-2xl md:text-3xl leading-tight">
            Pessoas que unem excelência técnica e propósito público.
          </h3>
          <ul className="mt-6 space-y-4 text-[15px] text-[#1A3A61]/75 leading-relaxed">
            <li>
              <strong className="text-[#1A3A61]">Especialistas jurídicos</strong> em
              licitações, contratos e direito administrativo, para curadoria e
              treinamento da IA.
            </li>
            <li>
              <strong className="text-[#1A3A61]">Engenheiros de software</strong> e
              cientistas de dados apaixonados por IA aplicada.
            </li>
            <li>
              <strong className="text-[#1A3A61]">Designers, produto e sucesso</strong>{" "}
              que entendem a realidade do servidor público e desenham experiências
              claras.
            </li>
          </ul>
        </aside>
      </div>
    </PageShell>
    </>
  );
}
